import { h, Component } from 'preact';
import { connect } from 'react-redux';
import reduce from '../reducers';
import * as actions from '../actions';

import constants from '../constants';
import util, { roundOneDecimalScaled } from '../util';
import Interactable from "../canvas/interactable";

import Renderer from '../canvas/renderer';
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  FontSizeOutlined,
  UnlockOutlined,
  LockOutlined,
  DeleteOutlined,
  DownOutlined
} from "@ant-design/icons";
import MoreToolBarOptions from './MoreToolBarOptions';
import FontFamilyWidget from './FontFamilyWidget';
import TextAlignWidget from './TextAlignWidget';
import TextSizeWidget from './TextSizeWidget';
import store from "../store";
import OpacityWidget from "./OpacityWidget";
import { setLineHeights } from "../canvas/line-height";
import PositionTools from "./PositionTools";
import GroupBarListItem from "./GroupBarListItem";
import { updatePageBars } from '../canvas/selection';
import { canDisplayAlign } from "../canvas/component";
import ResizeTextWidget from './ResizeTextWidget';
import TextFillWidget from './TextFillWidget';
import TextLineWidget from './TextLineWidget';
import { wrapToCurve } from '../canvas/word-art';
import WordArtCurveControls from './WordArtCurveControls';
import WordArtShadowControls from './WordArtShadowControls';
import LineHeight from './svg/LineHeight';
import TextGraphicAnimations from './TextGraphicAnimations';
import text from "../canvas/text";

export default @connect(reduce, actions)
class TextBar extends Component {
  constructor() {
    super();

    this.manualChangeSize = this.manualChangeSize.bind(this);

    this.toggleBold = () => {
      Renderer.updateElementStyle(this.props.isMultiSelect ? this.props.$elements : this.props.$element, 'fontWeight', this.props.isBold ? 'normal' : 'bold');
      this.props.updateElementAttribute('isBold', !this.props.isBold);
      if (this.props.textWrapped) {
        wrapToCurve(this.props.$element);
      }
    };

    this.toggleItalic = (ev) => {
      Renderer.updateElementStyle(this.props.isMultiSelect ? this.props.$elements : this.props.$element, 'fontStyle', this.props.isItalic ? 'normal' : 'italic');
      this.props.updateElementAttribute('isItalic', !this.props.isItalic);

      if (this.props.textWrapped) {
        wrapToCurve(this.props.$element);
      }
    };

    this.toggleUnderline = (ev) => {
      Renderer.updateElementStyle(this.props.isMultiSelect ? this.props.$elements : this.props.$element, 'textDecoration', this.props.isUnderline ? 'none' : 'underline');
      this.props.updateElementAttribute('isUnderline', !this.props.isUnderline);

      if (this.props.textWrapped) {
        wrapToCurve(this.props.$element);
      }
    };

    this.toggleUppercase = (ev) => {
      Renderer.updateElementStyle(this.props.isMultiSelect ? this.props.$elements : this.props.$element, 'textTransform', this.props.isUppercase ? 'none' : 'uppercase');
      this.props.updateElementAttribute('isUppercase', !this.props.isUppercase);

      if (this.props.textWrapped) {
        wrapToCurve(this.props.$element);
      }
    };

    this.changeFont = (font) => {
      Renderer.updateElementStyle(this.props.isMultiSelect ? this.props.$elements : this.props.$element, 'fontFamily', font.font);
      this.props.updateElementAttribute('fontFamily', font.label);
      this.props.updateElementAttribute('fontFamilyKey', font.key);

      if (this.props.textWrapped) {
        wrapToCurve(this.props.$element);
      }
    };

    this.changeSize = (size) => {
      const dims = Renderer.getDimensions();
      if (dims.unit === 'in') {
        const pixels = util.getPointsInPixels(size, dims);
        const percent = util.pixelToPercent(pixels, dims.fontSize);
        Renderer.updateElementStyle(this.props.isMultiSelect ? this.props.$elements : this.props.$element, 'fontSize', `${percent}%`, { fontSize: size });
      } else {
        const percent = size * constants.PIXEL_FONT_SIZE_ADJUSTMENT;
        Renderer.updateElementStyle(this.props.isMultiSelect ? this.props.$elements : this.props.$element, 'fontSize', `${percent}%`, { fontSize: size });
      }

      this.props.updateElementAttribute('fontSize', size);

      if (this.props.textWrapped) {
        wrapToCurve(this.props.$element, size);
      }

      updatePageBars(this.props.$element);

      // util.nextTick(() => {
      const element = this.props.$element;

      // console.log(element);
      // console.log(util.isOffPage(element));

      if (util.isOffPage(element)) {

        const transform = element[0].style.transform;
        const translateRegex = /translate\(([^)]+)\)/;

        let newTransform = transform.replace(translateRegex, '').trim();

        if (newTransform === '' || newTransform === 'none') {
          newTransform = 'translate(1px, 1px)';
        } else {
          newTransform += ' translate(1px, 1px)';
        }

        element.css('transform', newTransform);
        element.css('top', '0');
        element.css('left', '0');
        // element.attr('data-x', '1%');
        // element.attr('data-y', '1%');

        // Interactable.of(element).refreshPosition();
        Interactable.of(element).updateRect();
        // Interactable.refreshElementGuidelines();

        // matrix.refresh();
        // console.log(matrix.toString());

        updatePageBars(element);
      }
      // });

    };

    this.removeElement = () => {
      this.props.deselect();
      Renderer.remove(this.props.isMultiSelect ? this.props.$elements : this.props.$element, this.props.isMultiSelect);
    };

    this.removeGroup = () => {
      this.props.deselect();
      Renderer.remove(this.props.$element.parents('.group'));
    };

    this.copyElement = () => {
      const $clone = Renderer.copy(this.props.isMultiSelect ? this.props.$elements : this.props.$element);
      store.dispatch(actions.select($clone));
    };

    this.copyGroup = () => {
      Renderer.copy(this.props.$element.parents('.group'));
    };

    this.ungroup = () => {
      Renderer.ungroup(this.props.$element);
    };

    this.toggleLock = () => {
      if (this.props.isLocked) {
        Renderer.unlock(this.props.isMultiSelect ? this.props.$elements : this.props.$element);
      } else {
        Renderer.lock(this.props.isMultiSelect ? this.props.$elements : this.props.$element);
      }
      this.props.updateElementAttribute('isLocked', !this.props.isLocked);
    };

    this.openColorPicker = (type) => {
      util.openColorPicker(type);
    }

    this.openWordArt = () => {
      util.openWordArt();
    }

    this.openAnimations = () => {
      util.openAnimations();
    }

    this.updateOpacity = (ev) => {
      const val = ev.target.value;
      const $elements = this.props.isMultiSelect ? this.props.$elements.map($element => $element.find('.txt')) : [this.props.$element.find('.txt')];

      Renderer.updateElementStyle($elements, 'opacity', val / 100);
      this.props.updateElementAttribute('opacity', val);

      if (this.props.textWrapped) {
        wrapToCurve(this.props.$element);
      }
    };

    this.updateLetterSpacing = (update, ev) => {
      const val = (ev.target.value / 10).toFixed(2);

      if (!util.isFirefox() || update) Renderer.updateElementStyle(this.props.isMultiSelect ? this.props.$elements : this.props.$element, 'letterSpacing', val + 'em');
      this.props.updateElementAttribute('letterSpacing', ev.target.value);

      if (this.props.textWrapped) {
        wrapToCurve(this.props.$element);
      }
    };

    this.updateLineHeight = (update, ev) => {
      const val = (ev.target.value / 10).toFixed(2);

      if (!util.isFirefox() || update) {
        Renderer.updateElementStyle(this.props.isMultiSelect ? this.props.$elements : this.props.$element, 'lineHeight', val + 'em');
        setLineHeights(this.props.$element);
      }

      this.props.updateElementAttribute('lineHeight', ev.target.value);
      updatePageBars(this.props.$element);
    };

    this.textAlign = direction => {
      Renderer.updateElementStyle(this.props.isMultiSelect ? this.props.$elements : this.props.$element, 'textAlign', direction);
      this.props.updateElementAttribute('textAlign', direction);
    };

    this.getColorCss = () => {
      const $element = this.props.$element;
      if (!$element) return;
      if ($element.attr('data-color-text')) {
        return util.getCssFromColorData($element.attr('data-color-text'));
      } else {
        return $element.css('color');
      }
    }

    this.curveOff = () => {
      const $element = this.props.$element;
      if (!$element) return;
      this.props.updateElementAttribute('textWrapped', false);
      $element.attr('data-text-wrapped', "false");
      this.props.updateElementAttribute('curveValue', "1");
      $element.attr('data-curve-value', "1");
    }

    this.curveOn = () => {
      const $element = this.props.$element;
      if (!$element) return;

      if (!this.props.textWrapped) {
        let passedFontSize = 0;
        passedFontSize = roundOneDecimalScaled(this.props.fontSize * this.getScale());
        wrapToCurve(this.props.$element, passedFontSize !== 0 ? passedFontSize : false);
      }
    }

    this.updateCurve = (ev) => {
      let val = ev.target.value;
      if (parseFloat(val) === 0) {
        val = 0.05;
      }
      let passedFontSize = 0;
      if (!this.props.textWrapped) {
        passedFontSize = roundOneDecimalScaled(this.props.fontSize * this.getScale());
      }
      this.props.updateElementAttribute('curveValue', val);
      this.props.$element.attr('data-curve-value', val);
      wrapToCurve(this.props.$element, passedFontSize !== 0 ? passedFontSize : false);
    };

    this.addSpecCharacter = (ev, char) => {
      ev.stopPropagation();
      const $element = this.props.$element;
      if (!$element) return;

      const quill = text.getQuill($element);
      const range = quill.getSelection();
      if (range) {
        if (range.length > 0) {
          quill.deleteText(range.index, range.length);
        }
        quill.insertText(range.index, char, {}, true);
      }
    }
  }

  manualChangeSize(size) {
    this.changeSize(size);
  }

  getScale() {
    return Interactable.of(this.$element).getScale();
  }

  render(props, state) {
    const asideView = store.getState().view;
    const removeOptions = props.isInGroup ?
      <div className="dropdown sizes">
        <a className={`dropdown-toggle ${!props.isMobileVersion ? 'icn rwd-icon hint--bottom' : 'mobile-tool-widget'}`} data-hint="Delete">
          <DeleteOutlined />
          {props.isMobileVersion && <span>Delete</span>}
        </a>
        <div className="dropdown-menu" style="right: 0; width: auto;">
          <ul className="slider-list">
            <li>
              <a onClick={this.removeElement}>Delete</a>
            </li>
            <li>
              <a onClick={this.removeGroup}>Remove Group</a>
            </li>
          </ul>
        </div>
      </div>
      :
      <a className={`${!props.isMobileVersion ? 'rwd-icon hint--bottom' : 'mobile-tool-widget'}`} data-hint="Delete" onClick={this.removeElement}>
        <DeleteOutlined />
        {props.isMobileVersion && <span>Delete</span>}
      </a>;

    const copyOptions = props.isMobileVersion ?
      <a className="mobile-tool-widget" onClick={this.copyElement}>
        <svg>
          <use xlinkHref={constants.appPrefix + "/svg/icons.svg#app-copy"} />
        </svg>
        <span>Copy</span>
      </a>
      : props.isInGroup ?
        <a className="icn rwd-icon hint--bottom" data-hint="Duplicate" onClick={this.copyGroup}><span>Copy</span>
          <svg>
            <use xlinkHref={constants.appPrefix + "/svg/icons.svg#app-copy"} />
          </svg>
        </a>
        :
        <a className="icn rwd-icon hint--bottom" data-hint="Duplicate" onClick={this.copyElement}><span>Copy</span>
          <svg>
            <use xlinkHref={constants.appPrefix + "/svg/icons.svg#app-copy"} />
          </svg>
        </a>;

    const isTextHighlightMode = !!props.isTextHighlight;

    if (!props.textAlign) {
      props.textAlign = 'left';
    }

    const elementOptions = !props.isLocked ?
      <ul className={'secondary' + (!!isTextHighlightMode ? ' hidden' : '')}>
        <li className={(!props.isMobileVersion ? 'hideElement' : '') + (!props.isInGroup ? ' hidden' : '')}>
          <a className="icn rwd-icon ungroup-btn" onClick={this.ungroup}>
            <span>Ungroup</span>
          </a>
        </li>
        {props.multiselect && <GroupBarListItem {...props} />}
        {canDisplayAlign(props.$element) && <li className={!props.isMobileVersion ? "hideElement" : ""}>
          <PositionTools />
        </li>}

        {!props.isMobileVersion && <span className={"separator" + (!props.isMobileVersion ? " hideElement" : "")} />}

        {/*!props.multiselect && <li className={!props.isMobileVersion ? "hideElement" : ""}>
          <HyperlinkWidget $element={props.$element} href={props.href} isHyperlink={props.isHyperlink} updateElementAttribute={props.updateElementAttribute} hasPdfPermission={props.hasPdfPermission} setActiveMember={props.setActiveMember} />
        </li>*/}

        {!props.isMobileVersion && <li className={!props.isMobileVersion ? "hideElement" : ""}>
          {copyOptions}
        </li>}
        <li className={!props.isMobileVersion ? "hideElement" : ""}>
          <a className={`${!props.isMobileVersion ? 'icn rwd-icon hint--bottom' : 'mobile-tool-widget'}`} data-hint="Lock" onClick={this.toggleLock}>
            <UnlockOutlined />
            {props.isMobileVersion && <span>Lock</span>}
          </a>
        </li>
        {!props.isMobileVersion &&
          <li>
            {removeOptions}
          </li>}
      </ul>
      :
      <ul className="secondary">
        <li>
          <a className={`${!props.isMobileVersion ? 'icn tgl act rwd-icon hint--bottom' : 'act mobile-tool-widget'}`} data-hint="Unlock" onClick={this.toggleLock}>
            <LockOutlined />
            {props.isMobileVersion && <span>Unlock</span>}
          </a>
        </li>
        <li>
          <a className="locked-label rwd-icon hint--bottom" data-hint="Unlock" onClick={this.toggleLock}>Locked</a>
        </li>
      </ul>;

    return (
      <div className="context-bar text-bar" style={!props.isFreemium ? "top: 80px;" : ""}>
        <ul>
          {/* {props.isMobileVersion &&
            <li>
              {removeOptions}
            </li>
          } */}
          <li>
            <FontFamilyWidget fontKey={props.fontFamilyKey} family={props.fontFamily} changeFont={this.changeFont} />
          </li>

          <li>
            <TextSizeWidget $element={props.$element} value={props.fontSize} changeSize={this.changeSize} onChange={this.manualChangeSize} />
          </li>

          <li className="saf-stop hideThirdSet" style={{ margin: 0 }}>
            <a onClick={() => this.openColorPicker(constants.pickerTypes.textFont)} className={props.isMobileVersion ? 'mobile-tool-widget' : ''}>
              <span className="color-box" style={`background: ${this.getColorCss()}; float: none;`}></span>
              {props.isMobileVersion && <span>Color</span>}
            </a>
          </li>

          {!props.isMobileVersion &&
            <li className="saf-stop hideFourthSet">
              <a tabindex="0" className={"icn tgl " + (props.isBold ? 'act' : '')} onClick={this.toggleBold}>
                <BoldOutlined />
              </a>
            </li>
          }
          {!props.isMobileVersion &&
            <li className="saf-stop hideFourthSet">
              <a tabindex="0" className={"icn tgl " + (props.isItalic ? 'act' : '')} onClick={this.toggleItalic}>
                <ItalicOutlined />
              </a>
            </li>
          }
          {!props.isMobileVersion &&
            <li className="saf-stop hideFourthSet">
              <a tabindex="0" className={"icn tgl " + (props.isUnderline ? 'act' : '')} onClick={this.toggleUnderline}>
                <UnderlineOutlined />
              </a>
            </li>
          }
          {!props.isMobileVersion &&
            <li className="saf-stop hideFourthSet">
              <a tabindex="0" className={"icn tgl " + (props.isUppercase ? 'act' : '')} onClick={this.toggleUppercase}>
                <FontSizeOutlined />
              </a>
            </li>
          }
          <li className="hideSecondSet" onMouseDown={(ev) => ev.preventDefault()}>
            <div className="dropdown spec-char sizes">
              <a tabIndex="0" className="dropdown-toggle icn rwd-icon saf-stop hint--bottom" data-hint="Special Characters">
                &#230;
              </a>

              <div className="dropdown-menu right spec-char-menu" data-close="no">
                <button class="spec-char" onClick={(ev) => this.addSpecCharacter(ev, 'é')}>&#233;</button>
                <button class="spec-char" onClick={(ev) => this.addSpecCharacter(ev, 'è')}>&#232;</button>
                <button class="spec-char" onClick={(ev) => this.addSpecCharacter(ev, 'ê')}>&#234;</button>
                <button class="spec-char" onClick={(ev) => this.addSpecCharacter(ev, 'á')}>&#225;</button>
                <button class="spec-char" onClick={(ev) => this.addSpecCharacter(ev, 'ñ')}>&#241;</button>
                <button class="spec-char" onClick={(ev) => this.addSpecCharacter(ev, 'ü')}>&#252;</button>
                <button class="spec-char" onClick={(ev) => this.addSpecCharacter(ev, 'ç')}>&#231;</button>
                <button class="spec-char" onClick={(ev) => this.addSpecCharacter(ev, 'à')}>&#224;</button>
                <button class="spec-char" onClick={(ev) => this.addSpecCharacter(ev, 'ô')}>&#244;</button>
                <button class="spec-char" onClick={(ev) => this.addSpecCharacter(ev, 'œ')}>&#339;</button>
              </div>
            </div>
          </li>

          {props.isMobileVersion &&
            <li className="hideSecondSet">
              <div className="dropdown text-style">
                {props.isMobileVersion && <a className="dropdown-toggle mobile-tool-widget">
                  <BoldOutlined />
                  <span>Style</span>
                </a>}

                <div className="dropdown-menu right" data-close="no">
                  <div className="mobile-dropdown-header">
                    <span>Style</span>
                    <div className="dropdown-toggle"><DownOutlined /></div>
                  </div>
                  <ul>
                    <li className="saf-stop">
                      <a tabindex="0" className={"icn tgl " + (props.isBold ? 'act' : '')} onClick={this.toggleBold}>
                        <BoldOutlined />
                        <span>Bold</span>
                      </a>
                    </li>
                    <li className="saf-stop">
                      <a tabindex="0" className={"icn tgl " + (props.isItalic ? 'act' : '')} onClick={this.toggleItalic}>
                        <ItalicOutlined />
                        <span>Italic</span>
                      </a>
                    </li>
                    <li className="saf-stop">
                      <a tabindex="0" className={"icn tgl " + (props.isUnderline ? 'act' : '')} onClick={this.toggleUnderline}>
                        <UnderlineOutlined />
                        <span>Underline</span>
                      </a>
                    </li>
                    <li className="saf-stop">
                      <a tabindex="0" className={"icn tgl " + (props.isUppercase ? 'act' : '')} onClick={this.toggleUppercase}>
                        <FontSizeOutlined />
                        <span>Uppercase</span>
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </li>
          }

          {!props.textWrapped &&
            <li className={'hideThirdSet'}>
              <TextAlignWidget align={props.textAlign} onAlign={this.textAlign} />
            </li>}

          <li className="hideSecondSet">
            <div className="dropdown sizes">
              {!props.isMobileVersion && <a tabIndex="0" className="dropdown-toggle icn rwd-icon saf-stop hint--bottom" data-hint="Spacing">
                <span>Spacing</span>
                <svg>
                  <use xlinkHref={constants.appPrefix + "/svg/icons.svg#app-spacing"} />
                </svg>
              </a>}
              {props.isMobileVersion && <a className="dropdown-toggle mobile-tool-widget">
                <LineHeight />
                <span>Spacing</span>
              </a>}

              <div className="dropdown-menu right" data-close="no">
                {props.isMobileVersion && <div className="mobile-dropdown-header">
                  <span>Spacing</span>
                  <div className="dropdown-toggle"><DownOutlined /></div>
                </div>}
                <ul className="slider-list">
                  <li className="slider-item">
                    <label className="font-spacing">Letter Spacing</label>
                    <div tabindex="0" className="toolbar-wrap">
                      <input className="slider" type="range" min="0" max="10" step=".1" value={props.letterSpacing} onChange={this.updateLetterSpacing.bind(this, true)}
                        onMouseMove={this.updateLetterSpacing.bind(this, false)} />
                    </div>
                    <input className="value" type="number" min="0" max="10" step=".1" disabled="disabled" value={props.letterSpacing} />
                  </li>

                  <li className="slider-item">
                    <label className="font-spacing">Line Height</label>
                    <div tabindex="0" className="toolbar-wrap">
                      <input className="slider" type="range" min="10" max="30" step="1" value={props.lineHeight} onChange={this.updateLineHeight.bind(this, true)}
                        onMouseMove={this.updateLineHeight.bind(this, false)} />
                    </div>
                    <input className="value" type="number" min="10" max="30" step="1" disabled="disabled" value={props.lineHeight} />
                  </li>
                </ul>
              </div>
            </div>
          </li>

          {!isTextHighlightMode && !props.isMobileVersion && <span className="separator hideThirdSet" />}

          <li className={(!!isTextHighlightMode ? ' hidden hideThirdSet' : 'hideSecondSet')}>
            <OpacityWidget onUpdate={this.updateOpacity.bind(this)} value={props.opacity} />
          </li>

          {!props.isMobileVersion && <span className="separator hideThirdSet" />}

          {!props.isMobileVersion && <li className={'hideSecondSet'}>
            <a className={`${asideView === 'word-art' || this.props.textWrapped || this.props.textShadowed ? "text-effect-active" : ""}`} onClick={this.openWordArt}>
              <span>Text Effects</span>
            </a>
          </li>}

          {!props.isMobileVersion && <span className="separator hideSecondSet" />}

          {props.isMobileVersion && <li className={'hideSecondSet'}>
            <div className="dropdown sizes effect-animation-mobile">
              <a className={`dropdown-toggle ${this.props.textWrapped ? "text-effect-active" : ""} ${this.props.isMobileVersion ? 'mobile-tool-widget' : ''}`}>
                <svg><use xlinkHref={constants.appPrefix + "/svg/icons.svg#app-wordart"} /></svg>
                {props.isMobileVersion && <span>Text Effects</span>}
              </a>

              <div className="dropdown-menu right" data-close="no">
                {props.isMobileVersion && <div className="mobile-dropdown-header">
                  <span>Text Effects</span>
                  <div className="dropdown-toggle"><DownOutlined /></div>
                </div>}
                <div className="option-container" style="height: 50vh; overflow: auto;">
                  <WordArtCurveControls />
                  <WordArtShadowControls />
                </div>
              </div>
            </div>
          </li>}

          {!props.isMobileVersion && !props.textWrapped && <li className={'hideSecondSet'}>
            <a className={`${asideView === 'animations' || props.isAnimated ? "text-effect-active" : ""}`} onClick={this.openAnimations}>
              <span>Animate</span>
            </a>
          </li>}

          {props.isMobileVersion && !props.textWrapped && <li className='hideSecondSet'>
            <div className="dropdown sizes effect-animation-mobile">
              <a className={`dropdown-toggle ${props.isAnimated ? "text-effect-active" : ""} mobile-tool-widget`}>
                <svg><use xlinkHref={constants.appPrefix + "/svg/icons.svg#app-animations"} /></svg>
                <span>Animate</span>
              </a>

              <div className="dropdown-menu right" data-close="no">
                {props.isMobileVersion && <div className="mobile-dropdown-header">
                  <span>Animate</span>
                  <div className="dropdown-toggle"><DownOutlined /></div>
                </div>}
                <div className="option-container" style="height: 50vh; overflow: auto;">
                  <TextGraphicAnimations/>
                </div>
              </div>
            </div>
          </li>}

          {!props.isMobileVersion && <span className="separator hideSecondSet" />}

          {!props.isMobileVersion && <li className="showMoreToolBar">
            <MoreToolBarOptions openColorPicker={this.openColorPicker} getColorCss={this.getColorCss} />
          </li>}

          <li className={!props.isMobileVersion ? "hideElement" : ""}>
            <TextFillWidget $element={props.$element} $elements={this.props.$elements} color={props.bgColor} opacity={props.bgOpacity} radius={props.borderRadius}
              isMultiSelect={this.props.isMultiSelect} updateElementAttribute={props.updateElementAttribute} openColorPicker={this.openColorPicker} />
          </li>

          {!props.isMobileVersion && <span className={"separator hideElement"} />}

          <li className={!props.isMobileVersion ? "hideElement" : ""}>
            <TextLineWidget
              $element={props.$element}
              $elements={this.props.$elements}
              isMultiSelect={this.props.isMultiSelect}
              updateElementAttribute={props.updateElementAttribute}
              openColorPicker={this.openColorPicker} />
          </li>

          {(props.isAdmin || props.isDesigner) && <span className={"separator" + (!props.isMobileVersion ? " hideElement" : "")} />}
          {(props.isAdmin || props.isDesigner) && <li className={!props.isMobileVersion ? "hideElement" : ""}>
            <ResizeTextWidget width={props.width} $elements={this.props.$elements} unit={props.designUnit || 'in'} $element={props.$element}
              isMultiSelect={this.props.isMultiSelect} updateElementAttribute={props.updateElementAttribute}
            />
          </li>}
        </ul>

        {elementOptions}
      </div>
    )
  }
}
